import { useContext } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography, theme as themeColors } from '../../theme/colors';

interface BulkActionsToolbarProps {
  selectedCount: number;
  onUpdatePrices: () => void;
  onChangeCategory: () => void;
  onAdjustStock: () => void;
  onArchive: () => void;
}

export const BulkActionsToolbar = ({
  selectedCount,
  onUpdatePrices,
  onChangeCategory,
  onAdjustStock,
  onArchive,
}: BulkActionsToolbarProps) => {
  const { mode } = useContext(ColorModeContext);

  const colors = mode === 'dark' ? {
    bg: palette.surface,
    bgElevated: palette.surfaceElevated,
    bgHover: palette.surfaceHover,
    border: palette.border,
    borderLight: palette.borderLight,
    text: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    bg: palette.light.surface,
    bgElevated: palette.light.surfaceElevated,
    bgHover: palette.light.surfaceHover,
    border: palette.light.border,
    borderLight: palette.light.borderLight,
    text: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const buttonStyles = {
    borderRadius: themeColors.radius.lg,
    borderColor: colors.border,
    color: colors.textSecondary,
    textTransform: 'none' as const,
    fontWeight: typography.fontWeight.medium,
    px: 2,
    '&:hover': {
      borderColor: colors.borderLight,
      backgroundColor: colors.bgHover,
    },
  };

  if (selectedCount === 0) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        mb: 3,
        borderRadius: themeColors.radius.xl,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.bgElevated,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        alignItems: { xs: 'stretch', sm: 'center' },
        justifyContent: 'space-between',
      }}
    >
      {/* Selection Info */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: themeColors.radius.md,
            backgroundColor: palette.primaryMuted,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              color: palette.primary,
              fontWeight: typography.fontWeight.bold,
              fontSize: typography.fontSize.sm,
            }}
          >
            {selectedCount}
          </Typography>
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: colors.text,
            fontWeight: typography.fontWeight.medium,
          }}
        >
          {selectedCount === 1 ? '1 item selected' : `${selectedCount} items selected`}
        </Typography>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AttachMoneyOutlinedIcon />}
          onClick={onUpdatePrices}
          sx={buttonStyles}
        >
          Update prices
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CategoryOutlinedIcon />}
          onClick={onChangeCategory}
          sx={buttonStyles}
        >
          Change category
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<InventoryOutlinedIcon />}
          onClick={onAdjustStock}
          sx={buttonStyles}
        >
          Adjust stock
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArchiveOutlinedIcon />}
          onClick={onArchive}
          sx={{
            ...buttonStyles,
            color: palette.error,
            borderColor: palette.errorMuted,
            '&:hover': {
              borderColor: palette.error,
              backgroundColor: palette.errorMuted,
            },
          }}
        >
          Archive
        </Button>
      </Box>
    </Paper>
  );
};
